// DashboardPageContainer.jsx
import React, { useEffect, useState } from 'react';
import DashboardPage from './DashboardPage';
import QuickReplyModal from '@/components/Modals/QuickReplyModal';
import InteractionDetailsModal from '@/components/Modals/InteractionDetailsModal';
import 'leaflet/dist/leaflet.css'; // можно удалить, если карта не используется
import api from '@/lib/api';

const SEEN_KEY = 'ft_seen_interactions';
const loadSeen = () => new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]'));
const saveSeen = (set) => localStorage.setItem(SEEN_KEY, JSON.stringify([...set]));

const DashboardPageContainer = () => {
  // campaigns
  const [campaigns, setCampaigns] = useState([]);

  // summary from backend
  const [totalContactsUnique, setTotalContactsUnique] = useState(0);
  const [totalContactsRows, setTotalContactsRows] = useState(0);
  const [sampleEmails, setSampleEmails] = useState([]);

  // contacts/interactions
  const [organizations, setOrganizations] = useState([]);
  const [contactInteractions, setContactInteractions] = useState([]);

  // UI state
  const [interactionFilterStatus, setInteractionFilterStatus] = useState('all');
  const [interactionSearchTerm, setInteractionSearchTerm] = useState('');
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [quickReplyOpen, setQuickReplyOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      // 1) кампании
      const { data: campaignsData } = await api.get('/api/campaigns/monitoring/campaigns/');
      setCampaigns(campaignsData);

      // 2) сводка контактов с бэка
      // ожидаем: { total_rows: number, unique_emails: number, sample: [{id,email}, ...] }
      const { data: summary } = await api.get('/api/contacts/summary/');
      setTotalContactsRows(summary?.total_rows ?? 0);
      setTotalContactsUnique(summary?.unique_emails ?? 0);
      setSampleEmails(Array.isArray(summary?.sample) ? summary.sample : []);

      // 3) плоский список контактов/интеракций для "Recent Responses"
      const { data } = await api.get('/api/campaigns/monitoring/contacts/');
      const flatContacts = Array.isArray(data) ? data
        : Array.isArray(data?.results) ? data.results
        : [];

      // сгруппировать по организации — только для отображения
      const orgs = [...new Set(flatContacts.map(c => c.organizationName).filter(Boolean))]
        .map(name => ({
          name,
          contacts: flatContacts.filter(r => r.organizationName === name),
        }));
      setOrganizations(orgs);

      // метки “прочитано”
      const seen = loadSeen();
      const interactionsWithRead = flatContacts.map(i => ({
        ...i,
        contactName: i.contact?.name || i.contact_name || 'Unknown',
        read: seen.has(String(i.id)),
      }));
      setContactInteractions(interactionsWithRead);
    };

    fetchData().catch(console.error);
  }, []);

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'Etc/GMT+4',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).format(d);
  };

  // фильтр для таблицы "Recent Responses"
  const filteredContactInteractions = contactInteractions.filter(i => {
    const matchesStatus =
      interactionFilterStatus === 'all' ||
      (interactionFilterStatus === 'new' && !i.read) ||
      (interactionFilterStatus === 'viewed' && i.read && !i.replied) ||
      (interactionFilterStatus === 'awaiting_reply' && i.read && !i.replied && i.type === 'reply') ||
      (interactionFilterStatus === 'replied' && i.replied);

    const matchesSearch = (i.contactName || '')
      .toLowerCase()
      .includes(interactionSearchTerm.toLowerCase());

    return matchesStatus && matchesSearch;
  });

  const markAsRead = (id) => {
    const seen = loadSeen();
    seen.add(String(id));
    saveSeen(seen);
    setContactInteractions(prev =>
      prev.map(i => (i.id === id ? { ...i, read: true } : i))
    );
  };

  const markAsReplied = (id) => {
    setContactInteractions(prev =>
      prev.map(i => (i.id === id ? { ...i, replied: true } : i))
    );
  };

  const handleQuickReply = (interaction) => {
    setSelectedInteraction(interaction);
    setQuickReplyOpen(true);
  };

  const handleSendReply = (id, replyText) => {
    console.log(`Replying to ${id}: ${replyText}`);
    markAsReplied(id);
  };

  return (
    <>
      <DashboardPage
        // верхние карточки
        campaigns={campaigns}

        // данные для Total Contacts (из бэка)
        totalContactsUnique={totalContactsUnique}
        totalContactsRows={totalContactsRows}
        sampleEmails={sampleEmails}

        // блок "Recent Responses"
        organizations={organizations}
        filteredContactInteractions={filteredContactInteractions}
        interactionFilterStatus={interactionFilterStatus}
        setInteractionFilterStatus={setInteractionFilterStatus}
        interactionSearchTerm={interactionSearchTerm}
        setInteractionSearchTerm={setInteractionSearchTerm}
        formatTimestamp={formatTimestamp}
        markAsRead={markAsRead}
        handleQuickReply={handleQuickReply}
        setSelectedInteraction={(i) => {
          setSelectedInteraction(i);
          setDetailsOpen(true);
        }}
      />

      <QuickReplyModal
        isOpen={quickReplyOpen}
        onClose={() => setQuickReplyOpen(false)}
        interaction={selectedInteraction}
        onSendReply={handleSendReply}
      />

      <InteractionDetailsModal
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        interaction={selectedInteraction}
        formatTimestamp={formatTimestamp}
        markAsRead={markAsRead}
        markAsReplied={markAsReplied}
      />
    </>
  );
};

export default DashboardPageContainer;